import {
  Box,
  Divider,
  styled,
  Tooltip,
  useTheme,
  withStyles
} from '@material-ui/core';
import { SETVIImage, SHighlightText, SText } from '@setvi/shared/components';
import { TextColor } from '@setvi/shared/enums';
import { Product } from '@setvi/shared/interfaces';
import { useTableStyles } from 'Components/Shared';
import { getStockInfo } from 'pages/products/components/products-list/useProductColumns/contents/stock-info';
import { toTitleCase } from '@setvi/shared/utils/formatCustomerData';

import { PromotionBadge } from './components';
import { getProductStatus } from '../status';
import { ProductStatusBadge } from '../product-status';

const ImagePreview = withStyles({
  tooltip: {
    padding: '1px !important',
    borderRadius: 16,
    backgroundColor: TextColor.SlateGrey,
    boxShadow:
      '0px 40px 80px -16px rgba(48, 54, 60, 0.14), 0px 4px 4px 0px rgba(48, 54, 60, 0.04)',
    '& .MuiTooltip-arrow::before': {
      backgroundColor: TextColor.SlateGrey
    }
  }
})(Tooltip);

export const IsStock = styled(Box)(({ theme }) => ({
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
  display: 'flex',
  borderRadius: 12,
  minWidth: 50,
  fontSize: 13,
  fontWeight: 600,
  height: 24,
  gap: 4,
  color: '#5D7083',
  padding: theme.spacing(0, 1),
  cursor: 'default',
  backgroundColor: 'transparent',
  border: '1px solid #AFBAC6'
}));

interface ProductNameProps {
  product: Product;
  searchQuery?: string;
  onPromotionClick?: (product: Product) => void;
  onSalesEnablementClick?: (product: Product) => void;
}

const imgSize = 63;

export const ProductName = ({
  product,
  searchQuery,
  onPromotionClick,
  // @ts-ignore
  onSalesEnablementClick
}: ProductNameProps) => {
  const classes = useTableStyles();
  const { palette } = useTheme();

  const regularPlaceholder = '/images/product-placeholder.svg';
  const rfqPlaceholder = '/images/empty/no-product.png';

  const isRfqPage = window?.location?.href?.includes('auto-request-for-quote');
  const placeholder = isRfqPage ? rfqPlaceholder : regularPlaceholder;

  const src = product.imageUrl || product.images?.[0]?.path || placeholder;

  return (
    <Box
      display="flex"
      alignItems="center"
      gridGap="16px"
      className="product-name">
      <ImagePreview
        title={
          <img
            src={src}
            alt="product-img"
            style={{
              width: 180,
              height: 180,
              objectFit: 'contain',
              backgroundColor: palette.common.white,
              borderRadius: 16,
              padding: 20
            }}
          />
        }
        placement="right"
        arrow>
        <Box
          style={{
            minWidth: 72,
            width: 72,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          <SETVIImage
            className={classes.tableImage}
            style={{
              height: imgSize,
              width: imgSize,
              minWidth: imgSize,
              borderRadius: 12,
              overflow: 'hidden',
              objectFit: 'contain',
              padding: 2,
              border: '1px solid #AFBAC6'
            }}
            src={src}
            alt={toTitleCase(product.name)}
          />
        </Box>
      </ImagePreview>
      <Box display="flex" flexDirection="column">
        <Box display="flex" alignItems="center" gridGap="4px">
          <SHighlightText
            lineHeight="24px"
            weight="semibold"
            value={toTitleCase(product?.name || '')}
            searchQuery={searchQuery}
            maxLines={2}
          />
          {product?.promotions?.length > 0 && onPromotionClick && (
            <Box ml={1}>
              <PromotionBadge onClick={() => onPromotionClick(product)} />
            </Box>
          )}
        </Box>
        <Box display="flex" alignItems="center" gridGap="6px" flexWrap="wrap">
          <Box display="flex" alignItems="center" gridGap="4px">
            <SHighlightText
              onClick={e => e.stopPropagation()}
              style={{ color: TextColor.SlateGrey, cursor: 'default' }}
              size="sm"
              lineHeight="20px"
              value={`SKU: ${product.sku}`}
              searchQuery={searchQuery}
            />

            {!!product?.sku && !!product?.unitOfMeasure && (
              <Divider
                orientation="vertical"
                flexItem
                style={{ backgroundColor: '#AFBAC6' }}
              />
            )}

            {!!product?.unitOfMeasure && (
              <SHighlightText
                style={{ color: TextColor.SlateGrey, cursor: 'default' }}
                size="sm"
                lineHeight="20px"
                value={`UOM: ${product.unitOfMeasure}`}
                searchQuery={searchQuery}
              />
            )}
          </Box>
          <Box display="flex" alignItems="center" gridGap={6}>
            {/* !!product?.isStockProduct && (
              <Tooltip arrow title="This product can be restocked quickly.">
                <ProductStatusBadge>
                  <SText size="xs" title="Stock" />
                </ProductStatusBadge>
              </Tooltip>
            ) */}

            <ProductStatusBadge>
              <SText size="xs" title={getProductStatus(product)} />
            </ProductStatusBadge>
            {!!product?.stockInfo && (
              <ProductStatusBadge>
                <SText size="xs" title={getStockInfo(product?.stockInfo)} />
              </ProductStatusBadge>
            )}
          </Box>

          {/* flags?.salesEnablement && onSalesEnablementClick && (
            <SalesEnablementButton
              onClick={() => onSalesEnablementClick(product)}
            />
          ) */}
        </Box>
      </Box>
    </Box>
  );
};
